const BAND = 'section[id], header[id], footer[id], nav[id], aside[id]';
/** Beside <main> only a header/footer counts: they are pinned, so applyLayout never has to move them. */
const BESIDE_MAIN = 'header[id], footer[id]';
export const PINNED = new Set(['header', 'footer']);

// Bands in document order: the reorderable bands (children of <main> when the
// draft has one — WHEREVER <main> is nested, e.g. inside <div class="page"> —
// else children of <body>), PLUS a header/footer band that is a direct child
// of <body> beside <main>'s ancestor chain. Header/footer beside <main> are
// part of the spine but never move across that boundary; applyLayout keeps
// them put. Walking body's children keeps everything in document order.
export function bandsOf(doc: Document): HTMLElement[] {
  const main = doc.body.querySelector('main');
  const out: HTMLElement[] = [];
  for (const el of Array.from(doc.body.children)) {
    if (main && el.contains(main)) {
      // el IS <main> or the wrapper it is nested in: <main>'s band children are the spine.
      for (const c of Array.from(main.children)) if (c.matches(BAND)) out.push(c as HTMLElement);
    } else if (el.matches(main ? BESIDE_MAIN : BAND)) {
      out.push(el as HTMLElement);
    }
  }
  return out;
}

// Top-level bands (not nested in another band) that bandsOf does not manage — e.g. a
// section beside <main>, or one inside a wrapper in <main> — so the layout can neither
// move nor hide them. Named in the panel rather than silently left out.
export function unmanagedBands(doc: Document): string[] {
  const managed = new Set<Element>(bandsOf(doc));
  return Array.from(doc.body.querySelectorAll(BAND))
    .filter((el) => !el.parentElement?.closest(BAND) && !managed.has(el))
    .map((el) => el.id);
}

export function applyLayout(html: string, order: string[], hidden: Set<string>): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const bands = bandsOf(doc);
  if (bands.length === 0) return html;
  const byId = new Map(bands.map((el) => [el.id, el]));
  for (const el of bands) el.hidden = hidden.has(el.id);
  // Reorder only within the one movable parent; a header/footer beside <main>
  // lives under <body> and must never be pulled into <main>. Its hidden flag is
  // set above; its position is left exactly where the writer put it.
  const reorderParent = doc.body.querySelector('main') ?? doc.body;
  const first = bands.find((el) => el.parentElement === reorderParent);
  if (first) {
    const anchor = reorderParent.insertBefore(doc.createComment(''), first);
    for (const id of order) {
      const el = byId.get(id);
      if (el && el.parentElement === reorderParent) reorderParent.insertBefore(el, anchor);
    }
    anchor.remove();
  }
  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
}
